import { useEffect, useMemo, useState } from "react";
import { Download, X, Monitor, Smartphone, FileText } from "lucide-react";

export default function Showcase() {
  const [shots, setShots] = useState([]);
  const [device, setDevice] = useState("desktop");
  const [group, setGroup] = useState("Alla");
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    fetch("/demo-images/manifest.json")
      .then((r) => r.json())
      .then((d) => setShots(d.shots || []))
      .catch(() => setShots([]));
  }, []);

  const groups = useMemo(
    () => ["Alla", ...Array.from(new Set(shots.map((s) => s.group)))],
    [shots]
  );
  const visible = shots.filter(
    (s) => s.device === device && (group === "Alla" || s.group === group)
  );

  return (
    <div className="min-h-screen bg-[#0d1117] text-slate-100" data-testid="showcase-page">
      <header className="border-b border-white/10 px-6 py-10 sm:px-10 lg:px-16">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
          FältMästare · produktdemo
        </p>
        <h1 className="mt-3 font-heading text-4xl font-bold sm:text-5xl lg:text-6xl">
          Hela flödet, från inloggning till fakturaunderlag
        </h1>
        <p className="mt-4 max-w-2xl text-base text-slate-400">
          {visible.length} skärmbilder från en riktig installation med planerade
          arbetsordrar, personal, tidsregistrering och uppföljning.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href="/demo-images/faltmastare-presentation.pdf"
            download
            data-testid="showcase-download-pdf"
            className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900 transition-transform hover:-translate-y-0.5"
          >
            <FileText className="h-4 w-4" /> Ladda ner PDF-presentation
          </a>
          <a
            href="/demo-images/faltmastare-demobilder.zip"
            download
            data-testid="showcase-download-all"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:border-amber-400 hover:text-amber-300"
          >
            <Download className="h-4 w-4" /> Alla bilder (ZIP)
          </a>
        </div>
      </header>

      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 border-b border-white/10 bg-[#0d1117]/90 px-6 py-4 backdrop-blur sm:px-10 lg:px-16">
        <div className="flex overflow-hidden rounded-full border border-white/15">
          {[
            { k: "desktop", label: "Dator", Icon: Monitor },
            { k: "mobile", label: "Mobil", Icon: Smartphone },
          ].map(({ k, label, Icon }) => (
            <button
              key={k}
              data-testid={`showcase-device-${k}`}
              onClick={() => setDevice(k)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                device === k ? "bg-amber-400 text-slate-900" : "text-slate-300 hover:bg-white/5"
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {groups.map((g) => (
            <button
              key={g}
              data-testid={`showcase-group-${g}`}
              onClick={() => setGroup(g)}
              className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                group === g
                  ? "border-amber-400 text-amber-300"
                  : "border-white/15 text-slate-400 hover:border-white/40"
              }`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      <main className="px-6 pb-24 pt-10 sm:px-10 lg:px-16">
        {visible.length === 0 ? (
          <p data-testid="showcase-empty" className="text-sm text-slate-400">
            Inga bilder hittades ännu.
          </p>
        ) : (
          <div
            className={`grid gap-10 ${
              device === "desktop" ? "lg:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-4"
            }`}
          >
            {visible.map((s, i) => (
              <figure
                key={s.file}
                data-testid={`showcase-card-${s.slug}`}
                className="group"
                style={{ animation: `fadeUp .5s ease both`, animationDelay: `${i * 45}ms` }}
              >
                <div className="mb-3 flex items-baseline justify-between gap-3">
                  <figcaption className="text-base font-semibold md:text-lg">
                    <span className="mr-2 text-amber-400">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </figcaption>
                  <a
                    href={`/demo-images/${s.file}`}
                    download
                    data-testid={`showcase-download-${s.slug}`}
                    className="shrink-0 text-slate-400 transition-colors hover:text-amber-300"
                    title="Ladda ner"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
                <button
                  onClick={() => setZoom(s)}
                  data-testid={`showcase-open-${s.slug}`}
                  className="block w-full overflow-hidden rounded-xl border border-white/10 bg-black/40 transition-transform duration-300 hover:-translate-y-1 hover:border-amber-400/50"
                >
                  <img
                    src={`/demo-images/${s.file}`}
                    alt={s.title}
                    loading="lazy"
                    className="w-full"
                  />
                </button>
              </figure>
            ))}
          </div>
        )}
      </main>

      {zoom && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setZoom(null)}
          data-testid="showcase-lightbox"
        >
          <button
            data-testid="showcase-lightbox-close"
            className="absolute right-5 top-5 text-slate-300 hover:text-white"
            onClick={() => setZoom(null)}
          >
            <X className="h-7 w-7" />
          </button>
          <img
            src={`/demo-images/${zoom.file}`}
            alt={zoom.title}
            className="max-h-[92vh] max-w-full rounded-lg"
          />
        </div>
      )}

      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}
